import { last, sortBy, uniqBy } from "lodash-es";
import { ItemType } from "../../../clients/graphqlTypes.js";
import { canHaveCreateInput as _canHaveCreateInput } from "../doc-items/tree-helpers.js";
import { getDefaultCreatableSubItemTypes } from "../doc-items/constraints.js";
import { getParentItem } from "../../trees/helpers.js";
import {
  getRankBehindSibling,
  ROOT_ITEM_ID,
} from "../../trees-virtual/helpers.js";
import type { AdditionalItemInput } from "../../trees-virtual/types.js";
import type { DocDataGrid_ItemFragment } from "./DocDataGrid.generated.js";
import type { DocDataGridRow } from "./types.js";

// ---------------------------------------------------------------------------
// Path building helpers
// ---------------------------------------------------------------------------

function buildItemPath<T extends DocDataGrid_ItemFragment>(
  item: T,
  itemsById: Map<string, T>,
  rootItemId: string | undefined,
  showRootItem: boolean
): string[] {
  if (
    !item.parentId ||
    item.parentId === ROOT_ITEM_ID ||
    item.parentId === rootItemId
  ) {
    return showRootItem && rootItemId ? [rootItemId, item.id] : [item.id];
  }
  const parent = itemsById.get(item.parentId);
  if (!parent)
    return showRootItem && rootItemId ? [rootItemId, item.id] : [item.id];
  return [
    ...buildItemPath(parent, itemsById, rootItemId, showRootItem),
    item.id,
  ];
}

// ---------------------------------------------------------------------------
// buildDocDataGridRows
// ---------------------------------------------------------------------------

export function buildDocDataGridRows<T extends DocDataGrid_ItemFragment>(
  items: T[],
  rootItem: T | undefined,
  selectedItem: T | null,
  additionalItemInput: AdditionalItemInput | null | undefined,
  enableCreating: boolean,
  documentItemTypes: ItemType[],
  filterItem?: (item: T) => boolean,
  shouldRenderCreateInput?: (item: T) => boolean,
  showRootItem: boolean = true
): DocDataGridRow<T>[] {
  const rootItemId = rootItem?.id;

  const selectedItemParent =
    selectedItem && selectedItem.parentId !== null
      ? getParentItem(selectedItem, items)
      : null;

  const additionalItem = additionalItemInput
    ? (items.find(i => i.id === additionalItemInput.itemId) ?? null)
    : null;

  const additionalItemParent =
    additionalItem && additionalItem.parentId !== null
      ? getParentItem(additionalItem, items)
      : null;

  // Find last children by rank for input row rank placement
  const {
    lastRootItemChild,
    lastSelectedItemChild,
    lastSelectedItemParentChild,
  } = items.reduce(
    (acc, item) => {
      if (rootItemId && item.parentId === rootItemId) {
        if (!acc.lastRootItemChild || item.rank > acc.lastRootItemChild.rank)
          acc.lastRootItemChild = item;
      }
      if (selectedItem && item.parentId === selectedItem.id) {
        if (
          !acc.lastSelectedItemChild ||
          item.rank > acc.lastSelectedItemChild.rank
        )
          acc.lastSelectedItemChild = item;
      }
      if (selectedItemParent && item.parentId === selectedItemParent.id) {
        if (
          !acc.lastSelectedItemParentChild ||
          item.rank > acc.lastSelectedItemParentChild.rank
        )
          acc.lastSelectedItemParentChild = item;
      }
      return acc;
    },
    {
      lastRootItemChild: null as T | null,
      lastSelectedItemChild: null as T | null,
      lastSelectedItemParentChild: null as T | null,
    }
  );

  const itemsById = new Map(items.map(item => [item.id, item]));

  // Pre-compute children grouped by parentId for O(1) lookup
  const childrenByParentId = new Map<string, T[]>();
  for (const item of items) {
    if (item.parentId) {
      let children = childrenByParentId.get(item.parentId);
      if (!children) {
        children = [];
        childrenByParentId.set(item.parentId, children);
      }
      children.push(item);
    }
  }

  // Build input rows (4 cases mirroring getTreeFromItems in trees-virtual/helpers.ts)
  type InputSpec = {
    inputParentId: string;
    inputRank: number;
    isAdditionalInput?: boolean;
  };

  const inputSpecs: InputSpec[] = enableCreating
    ? uniqBy(
        [
          // Case 4 (additional item input from context menu, above or below a
          // specific item) is listed first so it wins `uniqBy` on a rank
          // collision with the Case 3 sibling-create input (e.g. "add below"
          // the last child), preserving `isAdditionalInput`.
          ...(additionalItemInput &&
          additionalItem &&
          additionalItemParent &&
          !(
            "deletedAt" in additionalItemParent &&
            additionalItemParent.deletedAt
          ) &&
          additionalItemParent.id !== ROOT_ITEM_ID &&
          shouldRenderCreateInput?.(additionalItemParent) !== false &&
          _canHaveCreateInput(
            additionalItemParent.type,
            documentItemTypes,
            Boolean(additionalItemParent.linkedQuoteTemplate)
          )
            ? [
                {
                  inputParentId: additionalItemParent.id,
                  inputRank:
                    additionalItem.rank +
                    (additionalItemInput.position === "below" ? 0.5 : -0.5),
                  isAdditionalInput: true,
                },
              ]
            : []),

          // Case 1: root item input (create child of root)
          ...(rootItem &&
          !("deletedAt" in rootItem && rootItem.deletedAt) &&
          rootItem.id !== ROOT_ITEM_ID &&
          shouldRenderCreateInput?.(rootItem) !== false &&
          _canHaveCreateInput(
            rootItem.type,
            documentItemTypes,
            Boolean(rootItem.linkedQuoteTemplate)
          )
            ? [
                {
                  inputParentId: rootItem.id,
                  inputRank: (lastRootItemChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),

          // Case 2: selected item input (create children of selected)
          ...(selectedItem &&
          !("deletedAt" in selectedItem && selectedItem.deletedAt) &&
          selectedItem.id !== rootItemId &&
          shouldRenderCreateInput?.(selectedItem) !== false &&
          _canHaveCreateInput(
            selectedItem.type,
            documentItemTypes,
            Boolean(selectedItem.linkedQuoteTemplate)
          )
            ? [
                {
                  inputParentId: selectedItem.id,
                  inputRank: (lastSelectedItemChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),

          // Case 3: selected item's parent input (create siblings of selected)
          ...(selectedItemParent &&
          !(
            "deletedAt" in selectedItemParent && selectedItemParent.deletedAt
          ) &&
          selectedItemParent.id !== rootItemId &&
          shouldRenderCreateInput?.(selectedItemParent) !== false &&
          _canHaveCreateInput(
            selectedItemParent.type,
            documentItemTypes,
            Boolean(selectedItemParent.linkedQuoteTemplate)
          )
            ? [
                {
                  inputParentId: selectedItemParent.id,
                  inputRank: (lastSelectedItemParentChild?.rank ?? 0) + 0.5,
                },
              ]
            : []),
        ],
        (spec: InputSpec) => `${spec.inputParentId}/${spec.inputRank}` // deduplicate by parentId & rank
      )
    : [];

  const inputRows: DocDataGridRow<T>[] = inputSpecs.map(
    ({ inputParentId, inputRank, isAdditionalInput }) => {
      const id = `input:${inputParentId}:${inputRank}`;
      const parentItem = itemsById.get(inputParentId) ?? null;

      // Compute path: if parent is root item, this input is top-level (or nested under root row)
      let path: string[];
      if (inputParentId === rootItemId) {
        path = showRootItem && rootItemId ? [rootItemId, id] : [id];
      } else {
        const parentPath = parentItem
          ? buildItemPath(parentItem, itemsById, rootItemId, showRootItem)
          : [];
        path = [...parentPath, id];
      }

      return {
        id,
        kind: "input" as const,
        // store parent item so InputComponent can access it (mirrors VirtualTreeItem pattern)
        item: parentItem,
        parentItem: parentItem?.parentId
          ? (itemsById.get(parentItem.parentId) ?? null)
          : null,
        itemChildren: childrenByParentId.get(inputParentId) ?? [],
        inputMeta: additionalItemInput ?? null,
        isAdditionalInput,
        path,
        depth: path.length - 1,
        rank: inputRank,
        parentId: inputParentId,
        type: null,
      };
    }
  );

  // Build regular item rows (exclude root item)
  const itemRows: DocDataGridRow<T>[] = items
    .filter(item => {
      if (item.isRootItem) return false;
      if (filterItem?.(item) === false) return false;
      return true;
    })
    .map(item => {
      const path = buildItemPath(item, itemsById, rootItemId, showRootItem);
      return {
        id: item.id,
        kind: "item" as const,
        item,
        parentItem: item.parentId
          ? (itemsById.get(item.parentId) ?? null)
          : null,
        itemChildren: childrenByParentId.get(item.id) ?? [],
        inputMeta: null,
        path,
        depth: path.length - 1,
        rank: item.rank,
        parentId: item.parentId ?? null,
        type: item.type,
        __reorder__: `${item.pathForPdf} ${item.title}`,
      };
    });

  // Optionally prepend root item as the top-level row
  const rootRow: DocDataGridRow<T>[] =
    showRootItem && rootItem && rootItemId
      ? [
          {
            id: rootItemId,
            kind: "item" as const,
            item: rootItem,
            parentItem: null,
            itemChildren: childrenByParentId.get(rootItemId) ?? [],
            inputMeta: null,
            path: [rootItemId],
            depth: 0,
            rank: 0,
            parentId: null,
            type: rootItem.type,
            __reorder__: `${rootItem.pathForPdf} ${rootItem.title}`,
          },
        ]
      : [];

  return sortBy([...rootRow, ...itemRows, ...inputRows], r => r.rank);
}

// ---------------------------------------------------------------------------
// canDropDocDataGridRow
// ---------------------------------------------------------------------------

export type DropPosition = "above" | "below" | "inside";

export interface CanDropParams<T extends DocDataGrid_ItemFragment> {
  sourceRowId: string;
  targetRowId: string;
  dropPosition: DropPosition;
  rows: DocDataGridRow<T>[];
  documentItemTypes: ItemType[];
  isLoading: boolean;
}

/**
 * Pure canDrop predicate for DocDataGrid row reordering.
 *
 * Mirrors the constraints of VirtualTableTree lines 437–491, except for the
 * input-row rule: that view rejects a drop *adjacent* to an input node (its rank
 * is derived from array position), while here rank comes from the target item's
 * own rank, so an input row next to an item row is irrelevant. An input row as
 * the *target* resolves to its parent's next child slot (see the input-row rule
 * below).
 */
export function canDropDocDataGridRow<T extends DocDataGrid_ItemFragment>({
  sourceRowId,
  targetRowId,
  dropPosition,
  rows,
  documentItemTypes,
  isLoading,
}: CanDropParams<T>): boolean {
  // Constraint 1: block while mutation is loading
  if (isLoading) return false;

  const rowsById = new Map(rows.map(r => [r.id, r]));
  const sourceRow = rowsById.get(sourceRowId);
  const targetRow = rowsById.get(targetRowId);
  if (!sourceRow || !targetRow) return false;

  // Input-row rule: an input row is presentation-only and holds no item of its
  // own. What it does occupy is its parent's *next child slot* — its rank is
  // `lastChild.rank + 0.5`, or `item.rank ± 0.5` for an "add above/below" input
  // — and that slot is the gap immediately *before* the row. So `"above"` is the
  // only position on an input row that names a real place, and the only one
  // accepted: MUI would otherwise draw a box around the text field for
  // `"inside"`, as if an item could nest into it, and a line past the end of the
  // group for `"below"`. `computeDropPlacement` turns the slot into the integer
  // rank `Math.ceil(inputRow.rank)`, so no fractional rank can reach
  // `SetTreeHierarchyInput.rank` (`Int!`).
  //
  // An input row *adjacent* to an item row does not constrain that item row at
  // all: it always follows a parent's last child, so keying on the neighbour
  // would make the last-child position unreachable.
  if (targetRow.kind === "input" && dropPosition !== "above") return false;

  // Determine the proposed parent row. An input row proposes its own parent —
  // the drop appends into that parent's next child slot.
  const proposedParentId =
    dropPosition === "inside" && targetRow.kind === "item"
      ? targetRow.id
      : targetRow.parentId;
  const proposedParentRow = proposedParentId
    ? rowsById.get(proposedParentId)
    : undefined;

  // Block dropping above/below root item (no siblings allowed). Input rows keep
  // their parent item in `.item`, so this must only apply to item rows.
  if (
    targetRow.kind === "item" &&
    targetRow.item?.isRootItem &&
    dropPosition !== "inside"
  ) {
    return false;
  }

  // An item can never become its own child. MUI's own `to-descendent` rule
  // covers item targets (and any deeper ancestor relation), but an input-row
  // target proposes its parent, which can be the dragged row itself — e.g. the
  // selected item's child-input row, which exists even when it has no children.
  if (proposedParentId === sourceRowId) return false;

  // Constraint 4: proposed parent is an input row
  if (proposedParentRow?.kind === "input") return false;

  // Constraint 5: proposed parent is synthetic root
  if (proposedParentId === ROOT_ITEM_ID) return false;

  // Constraint 2: proposed parent has linkedQuoteTemplate
  if (proposedParentRow?.item?.linkedQuoteTemplate) return false;

  // an item dropped into an eliminated subtree would vanish from every view
  // right away
  if (
    proposedParentRow?.item?.isItemEliminated ||
    proposedParentRow?.item?.isAnyParentItemEliminated
  ) {
    return false;
  }

  // Constraint 3: parent type doesn't accept source item's type
  if (proposedParentRow?.type && sourceRow.type) {
    const creatableTypes = getDefaultCreatableSubItemTypes(
      proposedParentRow.type,
      documentItemTypes
    );
    if (!creatableTypes.includes(sourceRow.type)) return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// computeDropPlacement
// ---------------------------------------------------------------------------

export interface DropPlacement {
  parentId: string;
  rank: number;
}

export interface ComputeDropPlacementParams<
  T extends DocDataGrid_ItemFragment,
> {
  sourceRowId: string;
  targetRow: DocDataGridRow<T>;
  dropPosition: DropPosition;
  rows: DocDataGridRow<T>[];
  /** Unfiltered document items — rows hidden from the grid still occupy ranks. */
  allItems: readonly T[];
  documentItemTypes: ItemType[];
  /** Expanded row IDs, matching the grid's expansion source. `undefined` means all expanded. */
  expandedRowIds: string[] | undefined;
}

/** First child position; the backend rank-shifts existing children down. */
const FIRST_CHILD_RANK = 1;

/** Visible row a drop above `targetRow` ends up behind, if any. */
function getPrecedingVisibleSiblingId<T extends DocDataGrid_ItemFragment>({
  rows,
  targetRow,
  sourceRowId,
}: {
  rows: DocDataGridRow<T>[];
  targetRow: DocDataGridRow<T>;
  sourceRowId: string;
}): string | null {
  const precedingSiblings = sortBy(
    rows.filter(
      row =>
        row.kind === "item" &&
        row.parentId === targetRow.parentId &&
        row.id !== sourceRowId &&
        row.rank < targetRow.rank
    ),
    row => row.rank
  );

  return last(precedingSiblings)?.id ?? null;
}

/**
 * Pure computation of the new parent + rank for a valid DocDataGrid drop.
 *
 * - an input-row target → its parent's next child slot, for every `dropPosition`.
 * - `inside` → first child of the target.
 * - `above`  → sibling immediately before the target.
 * - `below`  → first child of the target when it has children, is expanded, and
 *              nesting is valid; otherwise sibling immediately after the target.
 *
 * The returned `rank` is expressed in the destination parent's **post-detach**
 * child order — see the `toPostDetachRank` comment in the body.
 *
 * Both sibling cases are resolved against `allItems`, because the rank the
 * backend expects counts siblings the grid does not show — eliminated items,
 * items filtered out by search — and counts them without the moved item.
 */
export function computeDropPlacement<T extends DocDataGrid_ItemFragment>({
  sourceRowId,
  targetRow,
  dropPosition,
  rows,
  allItems,
  documentItemTypes,
  expandedRowIds,
}: ComputeDropPlacementParams<T>): DropPlacement {
  const sourceRow = rows.find(r => r.id === sourceRowId);

  /**
   * `setTreeHierarchy` detaches the moved item before re-inserting it at
   * `rank2Index(rank)` (`documentTree.moveTreeNode`), so `rank` addresses the
   * destination parent's children *after* the detach. The virtual trees hit the
   * same convention by reading the rank off already-mutated tree data
   * (`VirtualTableTree.tsx` `onMoveNode`), whereas the ranks here come from the
   * unmutated rows.
   *
   * That only differs when the moved item is already a child of the destination
   * parent and currently sits above the insertion point: detaching it shifts
   * every following sibling up by one, so the row-derived rank is one too high.
   * Uncorrected, `lastChild.rank + 1` exceeds the post-detach child count and
   * `moveTreeNode` throws (`Childindex to big …`), while smaller ranks silently
   * land one slot too far down.
   */
  const toPostDetachRank = (placement: DropPlacement): DropPlacement =>
    sourceRow !== undefined &&
    sourceRow.parentId === placement.parentId &&
    sourceRow.rank < placement.rank
      ? { ...placement, rank: placement.rank - 1 }
      : placement;

  // An input row stands for its parent's next child slot, not for an item
  // position, so a drop on it appends into that slot. `Math.ceil` maps the row's
  // presentational half-rank onto the integer rank of the slot it visually
  // occupies: `lastChild.rank + 0.5` → `lastChild.rank + 1` (append), `0.5` → `1`
  // for an empty parent, and `item.rank ± 0.5` → the slot an "add above/below"
  // input sits in. Kept independent of `dropPosition` because the slot is a
  // property of the row, not of where in it the pointer landed — only `"above"`
  // is a valid drop (`canDropDocDataGridRow`'s input-row rule).
  if (targetRow.kind === "input") {
    // Input rows are always built with a non-null parentId
    // (`buildDocDataGridRows`), so falling back to "" would silently skip the
    // mutation in `onRowOrderChange` — assert the invariant instead.
    if (targetRow.parentId === null) {
      throw new Error("input row without a parentId cannot be a drop target");
    }
    return toPostDetachRank({
      parentId: targetRow.parentId,
      rank: Math.ceil(targetRow.rank),
    });
  }

  if (dropPosition === "inside") {
    return toPostDetachRank({
      parentId: targetRow.id,
      rank: FIRST_CHILD_RANK,
    });
  }

  if (dropPosition === "above") {
    const parentId = targetRow.parentId ?? "";
    // `getRankBehindSibling` already counts siblings without the moved item, so
    // only the row-derived fallback needs the post-detach correction.
    const rank = getRankBehindSibling({
      allItems,
      newParentId: parentId,
      movedItemId: sourceRowId,
      precedingSiblingId: getPrecedingVisibleSiblingId({
        rows,
        targetRow,
        sourceRowId,
      }),
    });
    return rank !== null
      ? { parentId, rank }
      : toPostDetachRank({ parentId, rank: targetRow.rank });
  }

  // dropPosition === "below": nest as first child when the target is an
  // expanded parent that can accept the dragged item; otherwise sibling-after.
  const targetHasChildren = rows.some(
    r => r.parentId === targetRow.id && r.kind === "item"
  );
  const targetExpanded =
    !expandedRowIds ||
    targetRow.item?.isRootItem === true ||
    expandedRowIds.includes(targetRow.id);
  const canNest =
    targetHasChildren &&
    targetExpanded &&
    canDropDocDataGridRow({
      sourceRowId,
      targetRowId: targetRow.id,
      dropPosition: "inside",
      rows,
      documentItemTypes,
      isLoading: false,
    });

  if (canNest) {
    return toPostDetachRank({
      parentId: targetRow.id,
      rank: FIRST_CHILD_RANK,
    });
  }

  const parentId = targetRow.parentId ?? "";
  const rank = getRankBehindSibling({
    allItems,
    newParentId: parentId,
    movedItemId: sourceRowId,
    precedingSiblingId: targetRow.id,
  });
  return rank !== null
    ? { parentId, rank }
    : toPostDetachRank({ parentId, rank: targetRow.rank + 1 });
}
